import { AlertTriangle, CheckCircle2, CircleHelp, Clock, FileWarning, Minus, OctagonAlert, ShieldAlert } from 'lucide-react';
import type { Icono, Tono } from '../components/ui';

// Cómo se LEE cada código de estado que manda el backend. Un solo sitio, para que la
// bandeja del Comité, la cabecera de la evaluación, el trámite regulatorio y las reglas
// digan «En aprobación» con las mismas letras.
//
// Los códigos son los de la máquina de estados del caso (`estado_del_caso`) y los del
// Formato Amarillo (`estado_formato_amarillo`). Un código desconocido se devuelve legible
// —guiones bajos a espacios, inicial en mayúscula— en vez de dejar el snake_case en pantalla.
//
// EL ACUSE DE LA AUTORIDAD NO SE TRADUCE AQUÍ (BUG-O11, MG-ILE-919). Hasta el 30-sep este
// glosario traducía `estado_cargo` («pendiente» → «Acuse pendiente») y el trámite y el
// expediente lo usaban, mientras la bandeja leía `texto_del_acuse`, que el servidor deriva con
// los días hábiles del país: del mismo caso una pantalla decía «pendiente» y otra
// «demorado» (O-11). El texto del acuse y el de la carta llegan del servidor
// (`texto_del_acuse`, `texto_de_la_carta`); aquí solo vive el TONO que los acompaña.

export const ESTADOS_DEL_CASO: Record<string, string> = {
  borrador: 'Borrador',
  registrado: 'Registrado',
  en_evaluacion: 'En evaluación',
  en_aprobacion: 'En aprobación',
  observado: 'Observado',
  aprobado: 'Aprobado',
  en_notificacion: 'En notificación',
  en_seguimiento: 'En seguimiento',
  cerrado: 'Cerrado',
  reabierto: 'Reabierto',
};

export const ESTADOS_DEL_FORMATO_AMARILLO: Record<string, string> = {
  pendiente: 'Pendiente',
  completo: 'Completo',
};

/** Un código desconocido se muestra legible, nunca en snake_case. */
export const legible = (codigo: string | null | undefined): string => {
  if (!codigo) return '—';
  const texto = codigo.replaceAll('_', ' ');
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

export const etiquetaDelEstadoDelCaso = (codigo: string | null | undefined): string =>
  (codigo ? ESTADOS_DEL_CASO[codigo] ?? legible(codigo) : '—');

export const etiquetaDelFormatoAmarillo = (codigo: string | null | undefined): string =>
  (codigo ? ESTADOS_DEL_FORMATO_AMARILLO[codigo] ?? legible(codigo) : '—');

// ---------------------------------------------------------------------------------------
// F-09 · TABLEROS (HU-34 bandeja general, HU-35 indicadores, HU-37 trazabilidad).
//
// El contrato de los tableros trae TEXTO para todo hecho derivado (`texto_del_semaforo`,
// `texto_de_cumplimiento`, `texto_del_resultado`, `hito.texto`). Lo que llega solo como
// código es lo de abajo: el TONO que acompaña a cada texto, el ÍCONO que nombra el servidor
// con una palabra semántica («elegir el glifo es del frontend», `objetos_de_valor.py`) y los
// pocos catálogos estáticos que la spec fija y el contrato no trae (gravedad, unidad, rama).
// ---------------------------------------------------------------------------------------

/**
 * El tono del estado del caso. Estaba como segundo glosario dentro de `Bandeja.tsx`
 * (lineamiento, Pendientes 16): el TEXTO es `ESTADOS_DEL_CASO`; aquí solo el color.
 */
export const TONOS_DEL_ESTADO_DEL_CASO: Record<string, Tono> = {
  borrador: 'neutral',
  registrado: 'info',
  en_evaluacion: 'info',
  en_aprobacion: 'info',
  observado: 'warning',
  aprobado: 'success',
  en_notificacion: 'warning',
  en_seguimiento: 'info',
  cerrado: 'success',
  reabierto: 'warning',
};

export const tonoDelEstadoDelCaso = (codigo: string | null | undefined): Tono =>
  (codigo ? TONOS_DEL_ESTADO_DEL_CASO[codigo] : undefined) ?? 'neutral';

/**
 * HU-34-C2: la gravedad del caso, «Grave · No grave · Sin decidir» (`ui/MG-ILE-67.md`). Son
 * también los valores del filtro (`gravedad` de `GET /tableros/bandeja`). La fila trae el
 * código y `null` mientras el Comité no decide.
 */
export const GRAVEDADES: Record<string, string> = {
  grave: 'Grave',
  no_grave: 'No grave',
  sin_decidir: 'Sin decidir',
};

export const etiquetaDeLaGravedad = (codigo: string | null | undefined): string =>
  GRAVEDADES[codigo ?? 'sin_decidir'] ?? legible(codigo);

export const TONOS_DE_LA_GRAVEDAD: Record<string, Tono> = { grave: 'danger', no_grave: 'neutral', sin_decidir: 'neutral' };

export const tonoDeLaGravedad = (codigo: string | null | undefined): Tono =>
  TONOS_DE_LA_GRAVEDAD[codigo ?? 'sin_decidir'] ?? 'neutral';

/**
 * El tono del semáforo de la bandeja (`semaforo.estado`). El TEXTO es `texto_del_semaforo`.
 * «Sin plazo configurado» NUNCA en verde (HU-34-C4): no es un «en plazo» con matiz.
 */
export const TONOS_DEL_SEMAFORO: Record<string, Tono> = {
  vencido: 'danger',
  por_vencer: 'warning',
  en_plazo: 'success',
  cumplido: 'info',
  sin_plazo_configurado: 'neutral',
  sin_plazo_regulatorio: 'neutral',
};

export const tonoDelSemaforo = (codigo: string | null | undefined): Tono =>
  (codigo ? TONOS_DEL_SEMAFORO[codigo] : undefined) ?? 'neutral';

/**
 * El aviso de un plazo calculado sin el calendario del país (`plazo_fiable = false`, HU-24).
 * Es el texto con que la Notificación regulatoria señala el mismo reloj (§8.1 R8).
 */
export const TEXTO_DEL_PLAZO_NO_FIABLE = 'cálculo no fiable: falta el calendario';

/**
 * El glifo de cada ícono semántico que nombra el servidor: el del semáforo de la bandeja
 * (`icono_del_semaforo`) y el del cumplimiento de un indicador (`indicador.icono`).
 */
export const ICONOS_DEL_SERVIDOR: Record<string, Icono> = {
  reloj: Clock,
  alerta: AlertTriangle,
  vencido: OctagonAlert,
  verificado: CheckCircle2,
  interrogacion: CircleHelp,
  guion: Minus,
};

export const iconoDelServidor = (codigo: string | null | undefined): Icono =>
  (codigo ? ICONOS_DEL_SERVIDOR[codigo] : undefined) ?? CircleHelp;

/**
 * HU-34b (`ui/MG-ILE-1092.md`): el glifo de cada motivo de «Atención requerida»
 * (`atencion_requerida[].motivo`). Siempre va con el `texto` del servidor, nunca solo.
 */
export const ICONOS_DEL_MOTIVO: Record<string, Icono> = {
  acuse_pendiente: FileWarning,
  informe_observado: ShieldAlert,
  esperando_evaluacion: Clock,
};

export const iconoDelMotivo = (motivo: string | null | undefined): Icono =>
  (motivo ? ICONOS_DEL_MOTIVO[motivo] : undefined) ?? CircleHelp;

/**
 * HU-34b: el tono de la cifra de un indicador del Dashboard. Es regla de presentación sobre un
 * número que ya llega contado (§8.1 R2): con algo que contar, el tono pedido; con 0, tinta (sin
 * tono). El significado lo da la etiqueta de la tarjeta, no el color (RNF-11).
 */
export const tonoDelConteo = (valor: number, tono: Tono): Tono | undefined => (valor > 0 ? tono : undefined);

/** El tono del cumplimiento de un indicador (`cumple_la_meta`); el texto es del servidor. */
export const tonoDelCumplimiento = (cumple: boolean | null | undefined): Tono => {
  if (cumple === true) return 'success';
  if (cumple === false) return 'danger';
  return 'neutral';
};

/**
 * La unidad con que se lee el valor de un indicador (`indicador.unidad`): «h, días hábiles, %,
 * iteraciones» (`ui/MG-ILE-68.md`). `razon` solo la usa «Iteraciones de revisión por informe».
 */
export const UNIDADES_DEL_INDICADOR: Record<string, string> = {
  horas: 'h',
  dias_habiles: 'días hábiles',
  porcentaje: '%',
  razon: 'iteraciones',
};

export const unidadDelIndicador = (codigo: string | null | undefined): string =>
  (codigo ? UNIDADES_DEL_INDICADOR[codigo] ?? legible(codigo) : '');

/**
 * El tono de la situación de un caso dentro de un indicador (`caso.resultado`, HU-35-C6). El
 * texto es `texto_del_resultado`. Los pares los define el catálogo de indicadores del servidor.
 */
export const TONOS_DEL_RESULTADO: Record<string, Tono> = {
  en_plazo: 'success',
  fuera_de_plazo: 'danger',
  dentro_de_la_meta: 'success',
  fuera_de_la_meta: 'danger',
  completo: 'success',
  incompleto: 'danger',
  cumplido: 'success',
  incumplido: 'danger',
};

export const tonoDelResultado = (codigo: string | null | undefined): Tono =>
  (codigo ? TONOS_DEL_RESULTADO[codigo] : undefined) ?? 'neutral';

/**
 * El encabezado de cada rama de la trazabilidad (`trazabilidad.ramas[]` llega como código,
 * vacío 13 de `ui/MG-ILE-69.md`). Son los títulos con que el servidor agrupa el documento
 * exportado (`TEXTO_DE_RAMA` de `dominio/trazabilidad.py`): pantalla e impreso dicen lo mismo.
 */
export const RAMAS_DE_LA_TRAZABILIDAD: Record<string, string> = {
  principal: 'Caso',
  avisos: 'Avisos',
  evaluacion: 'Evaluación',
  aprobacion: 'Aprobación',
  regulatoria: 'Regulatoria',
  seguimiento: 'Seguimiento',
};

export const etiquetaDeLaRama = (codigo: string | null | undefined): string =>
  (codigo ? RAMAS_DE_LA_TRAZABILIDAD[codigo] ?? legible(codigo) : '');

/** HU-37-C5: el aviso de privacidad de la trazabilidad (`ui/MG-ILE-69.md`, el mismo del impreso). */
export const TEXTO_PRIVACIDAD_DE_LA_TRAZABILIDAD =
  'Esta trazabilidad muestra los hitos del proceso. El contenido de la sección de personas no se muestra.';

/**
 * Los cuatro estados del FAR-03 (`informe.estado`, máquina del AT S3 §6.1). El contrato no trae
 * `texto_del_estado` del informe (vacío anotado en la entrega de 17-5): se lee aquí, en un solo
 * sitio, para que la evaluación del Comité y la aprobación digan «En aprobación» igual. Antes
 * vivía como mapa propio dentro de `InformeFar03.tsx`.
 */
export const ESTADOS_DEL_INFORME: Record<string, { texto: string; tono: Tono }> = {
  borrador: { texto: 'Borrador', tono: 'neutral' },
  en_revision: { texto: 'En aprobación', tono: 'info' },
  observado: { texto: 'Observado', tono: 'warning' },
  aprobado: { texto: 'Aprobado', tono: 'success' },
};

export const estadoDelInforme = (codigo: string | null | undefined): { texto: string; tono: Tono } =>
  (codigo ? ESTADOS_DEL_INFORME[codigo] : undefined) ?? { texto: legible(codigo), tono: 'neutral' };

/**
 * HU-40: qué acto deja la constancia (`constancia.accion`). «Aprobó» y «Observó» los fija la
 * spec (`ui/MG-ILE-65.md`); el texto de `firma` (HU-40-C6) lo deja a F2b y no se inventa: sale
 * por `legible()` y queda anotado como vacío del contrato.
 */
export const ACCIONES_DE_CONSTANCIA: Record<string, string> = {
  aprobacion: 'Aprobó',
  observacion: 'Observó',
};

export const etiquetaDeLaAccionDeConstancia = (codigo: string | null | undefined): string =>
  (codigo ? ACCIONES_DE_CONSTANCIA[codigo] ?? legible(codigo) : '—');

/** HU-40-C1/C6: sobre qué documento es la constancia (`constancia.documento_tipo`). */
export const DOCUMENTOS_DE_CONSTANCIA: Record<string, string> = {
  far03: 'Informe FAR-03',
  carta: 'Carta a la autoridad',
};

export const etiquetaDelDocumentoDeConstancia = (codigo: string | null | undefined): string =>
  (codigo ? DOCUMENTOS_DE_CONSTANCIA[codigo] ?? legible(codigo) : '—');

/**
 * El tono de la insignia del acuse (BUG-O11, `ui/MG-ILE-54.md`: «con `acuse_demorado` para el
 * tono»). El TEXTO lo manda el servidor; aquí solo se elige el color que lo acompaña: demorado
 * por el booleano del contrato, y si no, por el código guardado del cargo.
 */
export const TONOS_DEL_CARGO: Record<string, Tono> = {
  pendiente: 'warning',
  recibido: 'success',
};

export const tonoDelAcuse = (estadoCargo: string | null | undefined, demorado: boolean): Tono => {
  if (demorado) return 'danger';
  return (estadoCargo ? TONOS_DEL_CARGO[estadoCargo] : undefined) ?? 'neutral';
};

/** El tono de la insignia de la carta: enviada es un paso cumplido; lo demás, en curso. */
export const TONOS_DE_LA_CARTA: Record<string, Tono> = {
  enviada: 'success',
};

export const tonoDeLaCarta = (estadoCarta: string | null | undefined): Tono =>
  (estadoCarta ? TONOS_DE_LA_CARTA[estadoCarta] : undefined) ?? 'neutral';

/**
 * HU-27-C7 y HU-28-C6 (`ui/MG-ILE-54.md`, «Estado del expediente»): el contrato trae el código
 * `expediente.estado` sin texto. «Archivado» lo fija la spec; «Abierto», el dominio.
 */
export const ESTADOS_DEL_EXPEDIENTE: Record<string, string> = {
  abierto: 'Abierto',
  archivado: 'Archivado',
};

export const etiquetaDelExpediente = (codigo: string | null | undefined): string =>
  (codigo ? ESTADOS_DEL_EXPEDIENTE[codigo] ?? legible(codigo) : '—');

export const TONOS_DEL_EXPEDIENTE: Record<string, Tono> = { archivado: 'neutral', abierto: 'info' };

/**
 * HU-27 (`ui/MG-ILE-55.md`): los estados a los que se puede llevar un compromiso. El estado
 * ACTUAL de cada fila se lee de `compromiso.texto_del_estado`; este glosario solo nombra los
 * `estados_siguientes` que ofrece el servidor (llegan como código).
 */
export const ESTADOS_DEL_COMPROMISO: Record<string, string> = {
  pendiente: 'Pendiente',
  en_curso: 'En curso',
  cumplida: 'Cumplida',
};

export const etiquetaDelCompromiso = (codigo: string | null | undefined): string =>
  (codigo ? ESTADOS_DEL_COMPROMISO[codigo] ?? legible(codigo) : '—');

export const TONOS_DEL_COMPROMISO: Record<string, Tono> = { pendiente: 'warning', en_curso: 'info', cumplida: 'success' };

export const tonoDelCompromiso = (codigo: string | null | undefined): Tono =>
  (codigo ? TONOS_DEL_COMPROMISO[codigo] : undefined) ?? 'neutral';

/** El tono de la clase de compromiso (`texto_de_clase` es el texto, del servidor). */
export const TONOS_DE_LA_CLASE: Record<string, Tono> = { accion: 'ai', disposicion: 'info', solicitud_de_informacion: 'warning' };

export const tonoDeLaClase = (tipo: string | null | undefined): Tono =>
  (tipo ? TONOS_DE_LA_CLASE[tipo] : undefined) ?? 'neutral';

/**
 * El tono de la fila de un documento del expediente (`filas[].estado`, AT S3 §2.2.4). El TEXTO
 * es `texto_del_estado`, del servidor; aquí solo el color que lo acompaña (con su ícono).
 */
export const TONOS_DEL_DOCUMENTO: Record<string, Tono> = {
  generado: 'success',
  pendiente: 'info',
  fallido: 'danger',
  hay_que_aportarlo: 'warning',
};

export const tonoDelDocumento = (codigo: string | null | undefined): Tono =>
  (codigo ? TONOS_DEL_DOCUMENTO[codigo] : undefined) ?? 'neutral';

/**
 * HU-28 · el acto de cada fila de `caso.cierres[]` del expediente: «Cierre 1», «Reapertura 1»
 * (`ui/MG-ILE-54.md`, «Historial de cierres y reaperturas»). El número es `orden`, del servidor.
 * «Reabierto 1 vez» ya NO se arma aquí: llega hecho en `caso.texto_de_reaperturas`.
 */
export const ACTOS_DEL_CIERRE: Record<string, string> = {
  cierre: 'Cierre',
  reapertura: 'Reapertura',
};

export const etiquetaDelActoDelCierre = (codigo: string | null | undefined): string =>
  (codigo ? ACTOS_DEL_CIERRE[codigo] : undefined) ?? legible(codigo);

/**
 * La insignia de la persona de fuera de ILENDER (D-S3-17). El contrato de `GET /sesion` trae el
 * booleano `es_externo` y no un texto; la lista de usuarios sí trae `texto_del_tipo`. El texto
 * es el que fijan las dos especificaciones (ui/MG-ILE-63 y ui/MG-ILE-915): «Usuario externo»,
 * no «invitado», para no confundirlo con el Especialista invitado.
 */
export const TEXTO_USUARIO_EXTERNO = 'Usuario externo';

/**
 * HU-29-C9 (MG-ILE-57): el rótulo de las semillas globales del maestro que el buscador del
 * registro ofrece mientras el país del caso no tiene filas. El servidor manda el booleano
 * `es_dato_de_prueba` por fila, no un texto; el texto es el literal del criterio.
 */
export const ROTULO_DATO_DE_PRUEBA = 'dato de prueba';

/**
 * HU-32b (MG-ILE-915): el TONO de la insignia del estado de un usuario. El texto llega del
 * servidor (`texto_del_estado`); aquí solo el color que lo acompaña (spec: success activo, neutral
 * desactivado). «Sin acceso» (activo sin roles, HU-32b-C10) no tiene tono en la spec: aviso.
 */
export const TONOS_DEL_ESTADO_DEL_USUARIO: Record<string, Tono> = {
  activo: 'success',
  sin_acceso: 'warning',
  desactivado: 'neutral',
};

export const tonoDelEstadoDelUsuario = (estado: string | null | undefined): Tono =>
  (estado ? TONOS_DEL_ESTADO_DEL_USUARIO[estado] : undefined) ?? 'neutral';

/**
 * HU-45b-C5 (MG-ILE-914): el TONO del estado de la plantilla de una autoridad. El texto llega del
 * servidor (`texto_de_plantilla`: «SENASA · v2» / «Sin plantilla»); aquí solo el color.
 */
export const TONOS_DEL_ESTADO_DE_PLANTILLA: Record<string, Tono> = {
  con_plantilla: 'success',
  // CC-02 (RN-81): sin plantilla propia, la carta usa la de la predeterminada y lo avisa.
  prestada: 'warning',
  sin_plantilla: 'warning',
};

export const tonoDelEstadoDePlantilla = (estado: string | null | undefined): Tono =>
  (estado ? TONOS_DEL_ESTADO_DE_PLANTILLA[estado] : undefined) ?? 'neutral';

/**
 * CC-02 (AT S3 §11.3.1, D-1): las marcas de una autoridad del país. Llegan como banderas del
 * contrato (`es_predeterminada`, `por_confirmar`, `activa`); el texto vive aquí, una sola vez.
 */
export const MARCAS_DE_AUTORIDAD = {
  predeterminada: 'Predeterminada',
  por_confirmar: 'Por confirmar con ILENDER',
  desactivada: 'Desactivada',
} as const;

// ---------------------------------------------------------------------------------------
// LAS RESPUESTAS DEL INSTRUCTIVO (29 puntos, HU-12), tal como se LEEN en el resumen del
// FAR-03 y de la ampliación (QA 01-10-2026, B10). Viajan en código (`si` / `no` /
// `no_aplica`); un punto descriptivo lee la terna como «Documentado / No se pudo obtener /
// No aplica», igual que la pestaña «Investigación».
// ---------------------------------------------------------------------------------------

export const RESPUESTAS_DEL_INSTRUCTIVO: Record<string, string> = {
  si: 'Sí', no: 'No', no_aplica: 'No aplica', documentado: 'Documentado', no_se_pudo_obtener: 'No se pudo obtener',
};
export const RESPUESTAS_DEL_PUNTO_DESCRIPTIVO: Record<string, string> = {
  ...RESPUESTAS_DEL_INSTRUCTIVO, si: 'Documentado', no: 'No se pudo obtener',
};

export const etiquetaDeLaRespuesta = (codigo: string | null | undefined, descriptivo = false): string => {
  if (!codigo) return 'Sin respuesta';
  return (descriptivo ? RESPUESTAS_DEL_PUNTO_DESCRIPTIVO : RESPUESTAS_DEL_INSTRUCTIVO)[codigo] ?? legible(codigo);
};
